import { MutableRefObject, ReactNode, useEffect, useRef } from 'react'
import { Control, FieldValues, Path, UseFormRegisterReturn, useWatch } from 'react-hook-form'

import TextArea from '@/components/view/atoms/textarea/Textarea'
import { FieldType } from '@/components/view/atoms/textfield/TextField'

interface Props<T extends FieldValues> {
  id?: string
  register: UseFormRegisterReturn
  control: Control<T>
  name: Path<T>
  error?: boolean
  counter?: boolean
  placeholder?: string
  memo?: boolean
  minRows?: number
  textareaRef?: MutableRefObject<HTMLTextAreaElement | null>
  flexGrow?: boolean
  rightBottomNode?: ReactNode
  leftBottomNode?: ReactNode
  fieldType?: FieldType
  shadow?: boolean
  autoResize?: boolean
  disabled?: boolean
  maxLength?: number
}

const FormTextArea = <T extends FieldValues>({
  id,
  register,
  control,
  name,
  error = false,
  counter = false,
  placeholder,
  memo = false,
  minRows,
  flexGrow,
  textareaRef,
  rightBottomNode,
  leftBottomNode,
  fieldType,
  shadow = false,
  autoResize = false,
  disabled = false,
  maxLength = undefined
}: Props<T>) => {
  const value = useWatch({ control, name })
  const internalRef = useRef<HTMLTextAreaElement | null>(null)
  const ref = textareaRef ?? internalRef

  useEffect(() => {
    if (autoResize && ref.current) {
      const el = ref.current
      el.style.height = 'auto'
      el.style.height = `${el.scrollHeight}px`
    }
  }, [value, autoResize, ref])

  return (
    <TextArea
      id={id}
      name={register.name}
      onChange={register.onChange}
      // react-hook-form が ref 経由で値を入れるため**非制御のまま**にする。
      // value を渡すと制御コンポーネントに変わり、IME入力中の挙動が変わる
      counterValue={value}
      onBlur={register.onBlur}
      inputRef={
        textareaRef
          ? (e) => {
              register.ref(e)
              textareaRef.current = e
            }
          : register.ref
      }
      required={register.required}
      disabled={register.disabled || disabled}
      maxLength={register.maxLength || maxLength}
      minLength={register.minLength}
      error={error}
      placeholder={placeholder}
      counter={counter}
      memo={memo}
      flexGrow={flexGrow}
      minRows={minRows}
      rightBottomNode={rightBottomNode}
      leftBottomNode={leftBottomNode}
      fieldType={fieldType}
      shadow={shadow}
    />
  )
}

export default FormTextArea
